/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { gql } from '@apollo/client';
import { Flex, Link } from '@bottlebooks/gatsby-theme-base';
import { graphql } from 'gatsby';
import React from 'react';
import { jsx } from 'theme-ui';
import ToggleFavorite from '../Favorites/ToggleFavorite';
import ListRow from '../List/ListRow';
import ListRowBody from '../List/ListRowBody';
import ProductBadge from '../Product/ProductBadge';
import ProductHeader, { ProductHeaderFragment } from '../Product/ProductHeader';
import ProductImage, { ProductImageFragment } from '../Product/ProductImage';
import ProductTastingNoteButton from '../TastingNote/ProductTastingNoteButton';
import { useFavorite } from '../TastingNote/useFavorite';
import ProductRequestSampleWithSnipcartButtonForList from './ProductRequestSampleWithSnipcartButtonForList';

export default function ProductListRow({ product, ...rest }) {
  const favorite = useFavorite(
    product && { type: 'product', id: product.productId }
  );
  return (
    <ListRow sx={{ alignItems: 'center', marginBottom: 3 }}>
      <Link
        sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap' }}
        {...rest}
      >
        <ProductListRowImage product={product} />
        <ListRowBody sx={{ position: 'relative', paddingRight: 5 }}>
          <ProductHeader product={product} />
        </ListRowBody>
        <ProductFeatures product={product} />
        <Flex
          direction={['column-reverse', 'row']}
          align="center"
          gap={0}
          sx={{ position: 'absolute', top: 3, right: 3 }}
        >
          {favorite.isLoading ? (
            <></>
          ) : (
            <Flex gap={2}>
              <ProductTastingNoteButton product={product} size="compact">
                <ProductTastingNoteButton.Icon />
              </ProductTastingNoteButton>
              <ToggleFavorite
                isToggled={favorite.isSet}
                variant="small"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  favorite.toggle();
                }}
              />
            </Flex>
          )}
        </Flex>
      </Link>
      <Flex
        direction="column"
        align="flex-end"
        sx={{ marginBottom: 3, marginX: 3, width: '100%' }}
      >
        {process.env.GATSBY_SNIPCART_API_KEY && (
          <ProductRequestSampleWithSnipcartButtonForList product={product} />
        )}
      </Flex>
    </ListRow>
  );
}

export function ProductListRowImage({ product }) {
  return <ProductImage product={product} sx={{ margin: 3, marginRight: 0 }} />;
}

export function ProductFeatures({ product }) {
  if (!product.features?.length) return null;
  return (
    <Flex
      sx={{
        width: '100%',
        paddingRight: 2,
        paddingBottom: 2,
        alignSelf: 'flex-end',
        position: 'relative', // Needed to stay above image backgrounds
        marginLeft: 'auto',
        justifyContent: 'flex-end',
      }}
    >
      {product.features?.map((type) => (
        // Product features have to be queried separately by the parent.
        <ProductBadge
          key={type}
          variant="onPrimary"
          type={type}
          sx={{ marginTop: -3 }}
        />
      ))}
    </Flex>
  );
}

// HEADSUP: Product features have to be queried separately by the parent.

export const fragment = graphql`
  fragment bb_ProductListRow on Bottlebooks_Product {
    ...bb_ProductRequestSampleWithSnipcartButton
    ...bb_ProductHeader
  }
  fragment ProductListRow on Product {
    ...ProductImage_list
    ...ProductHeader
  }

  # A fragment for the first page of products. It has more detailed images.
  fragment ProductListRow_first on Product {
    ...ProductImage
  }
`;

ProductListRow.fragment = gql`
  fragment ProductListRow on Product {
    ...ProductImage
    ...ProductHeader
  }
  ${ProductImageFragment}
  ${ProductHeaderFragment}
`;
